import React, { Component } from 'react';
import moment from 'moment';
import { Badge, Button, UncontrolledTooltip, Collapse } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFlag, faChevronDown, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

class BatchBoxJobItem extends Component {
    state = { statusExpanded: false };

    toggleStatusExpanded = () => {
        this.setState((p) => ({ statusExpanded: !p.statusExpanded }));
    }

    // Normalizes a queue status line into { status, startedAt, endedAt }.
    // `status` may arrive as a plain string (e.g. "completed") or as a nested
    // queue record with timestamps but no status string — in which case we
    // derive the status from the timestamps (ended -> completed, started ->
    // in progress, otherwise not started).
    getStageInfo = (line) => {
        const rec = (line.status && typeof line.status === 'object') ? line.status : {};
        const startedAt = line.started_at || rec.started_at || null;
        const endedAt = line.ended_at || rec.ended_at || null;

        let status;
        if (typeof line.status === 'string' && line.status.trim() !== '') {
            status = line.status;
        } else if (endedAt) {
            status = 'completed';
        } else if (startedAt) {
            status = 'in progress';
        } else {
            status = 'not started';
        }

        return { status, startedAt, endedAt };
    }

    // Maps a queue stage `status` to a reactstrap badge color.
    // Only `completed` is confirmed from sample data; the rest are best-guess
    // and easy to adjust once the full status vocabulary is known.
    getQueueStatusColor = (status) => {
        const statusColors = {
            completed: 'success',
            done: 'success',
            finished: 'success',
            in_progress: 'primary',
            running: 'primary',
            started: 'primary',
            printing: 'primary',
            active: 'primary',
            pending: 'warning',
            queued: 'warning',
            waiting: 'warning',
            failed: 'danger',
            error: 'danger',
            cancelled: 'danger',
            not_started: 'secondary',
            on_hold: 'secondary',
            paused: 'secondary'
        };
        return statusColors[String(status).toLowerCase().replace(/\s+/g, '_')] || 'secondary';
    }

    // API sends "DD/MM/YYYY HH:mm"; render as e.g. "June, 05 10:30 AM".
    formatStageDate = (dateString) => {
        if (!dateString) return null;
        const m = moment(dateString, 'DD/MM/YYYY HH:mm');
        return m.isValid() ? m.format('MMMM, DD hh:mm A') : dateString;
    }

    // Blank start -> "Not Started" (and never also show "Not Finished").
    // Started but no end -> "<start> → Not Finished".
    renderStageDates = (line) => {
        const { startedAt, endedAt } = this.getStageInfo(line);
        const started = this.formatStageDate(startedAt);
        if (!started) return 'Not Started';
        const ended = this.formatStageDate(endedAt);
        return `${started} → ${ended || 'Not Finished'}`;
    }

    handleEditQueueItem = (queueItem) => {
        const { boxJob, boxConfigurationId, boxConfigurationBatchId, onOpenModal } = this.props;
        onOpenModal(boxJob, 'edit', boxConfigurationId, boxConfigurationBatchId, queueItem);
    }

    handlePrintAsIsQueueItem = (queueItem) => {
        const { boxJob, boxConfigurationId, boxConfigurationBatchId, onOpenModal } = this.props;
        onOpenModal(boxJob, 'printAsIs', boxConfigurationId, boxConfigurationBatchId, queueItem);
    }

    handleAddUVJob = () => {
        const { boxJob, boxConfigurationId, boxConfigurationBatchId } = this.props;
        const orderNumber = boxJob.order_number;
        const boxJobId = boxJob.id;
        window.location.href = `/box-jobs/${orderNumber}?ebjid=${boxJobId}&box_configuration_id=${boxConfigurationId}&box_configuration_batch_id=${boxConfigurationBatchId}`;
    }

    renderQueueItem = (qi, idx) => {
        const { boxJob } = this.props;
        const isFlagged = qi.box_job_flags && qi.box_job_flags.length > 0 && !qi.ignore_flags;
        const disableEdit = qi.box_job_flags && qi.box_job_flags.some((f) => f.disable_edit === true);
        const showActions = isFlagged && !disableEdit && boxJob && boxJob.quantity > 0;
        const containerClass = isFlagged
            ? 'border border-danger bg-warning rounded p-2 mb-1'
            : 'p-1 mb-1';

        let designText = '';
        if (qi.ignore_flags === true) designText += 'PRINT AS IS — ';
        if (qi.box_design_name && qi.box_design_name !== '') {
            designText += `Drill: ${qi.box_design_name}`;
        } else if (qi.box_uv_design_name && qi.box_uv_design_name !== '') {
            designText += `UV (Side ${qi.box_side}): ${qi.box_uv_design_name}`;
        }

        let servicesText = '';
        if (qi.box_job_queue_item_services && qi.box_job_queue_item_services.length > 0) {
            servicesText = qi.box_job_queue_item_services
                .map((s) => `${s.qty} ${s.name}`)
                .join(', ');
        }

        return (
            <div key={qi.id || idx} className={containerClass} style={{ fontSize: '0.75rem' }}>
                <div className="d-flex justify-content-between align-items-start">
                    <div style={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                        {designText && <div>{designText}</div>}
                        {servicesText && <div className="text-muted">{servicesText}</div>}
                        {isFlagged && qi.box_job_flags.map((flag, i) => (
                            <div
                                key={i}
                                className="alert alert-danger p-1 mb-1 mt-1 mr-1"
                                style={{ display: 'inline-block', fontSize: '0.7rem' }}
                            >
                                <FontAwesomeIcon icon={faFlag} className="mr-1" />
                                {flag.customer_label}
                            </div>
                        ))}
                        {isFlagged && qi.note_image_url && (
                            <div className="mt-1">
                                <a href={qi.note_image_url} target="_blank" rel="noopener noreferrer">
                                    <img
                                        src={qi.note_image_thumb_url}
                                        alt="Note"
                                        className="img-fluid"
                                        style={{ maxWidth: '80px' }}
                                    />
                                </a>
                            </div>
                        )}
                    </div>
                    {showActions && (
                        <div className="text-right ml-2">
                            <Button
                                id={`editQI-${qi.id}`}
                                color="danger"
                                size="sm"
                                onClick={() => this.handleEditQueueItem(qi)}
                                style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}
                                className="mb-1 mr-1"
                            >
                                Edit
                            </Button>
                            <UncontrolledTooltip placement="top" target={`editQI-${qi.id}`}>
                                Change the {qi.service_type === 'drill' ? 'drill' : 'UV'} design for this item.
                            </UncontrolledTooltip>

                            <Button
                                id={`printQI-${qi.id}`}
                                color="info"
                                size="sm"
                                onClick={() => this.handlePrintAsIsQueueItem(qi)}
                                style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}
                                className="mb-1"
                            >
                                Print As Is
                            </Button>
                            <UncontrolledTooltip placement="top" target={`printQI-${qi.id}`}>
                                Ignore the flag and print this item as is.
                            </UncontrolledTooltip>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { boxJob, configurationName } = this.props;

        if (!boxJob) {
            return null;
        }

        const queueItems = boxJob.box_job_queue_items || [];
        const statusLines = boxJob.queue_status_lines || [];
        const { statusExpanded } = this.state;

        return (
            <div className="border-top pt-1 pb-2 mb-1 px-2 bg-white" style={{ fontSize: '0.85rem' }}>
                <div className="row">
                    <div className="col-6">
                        <div className="mb-1">
                            <strong>Job #{boxJob.id}</strong>
                            {configurationName && <span className="text-muted ml-2">({configurationName})</span>}
                        </div>
                        <div className="mb-1">
                            <div style={{ fontSize: '0.75rem' }}>
                                {boxJob.enclosure_type} {boxJob.powder_color} - Qty: {boxJob.quantity}
                            </div>
                            {boxJob.quantity <= 0 && (
                                <Badge
                                    color="secondary"
                                    className="mt-1"
                                    style={{ fontSize: '0.7rem', whiteSpace: 'normal' }}
                                >
                                    No longer valid — this job is being redone in another order
                                </Badge>
                            )}
                        </div>
                    </div>
                    <div className="col-6 text-right">
                        {statusLines.length > 0 ? (
                            <div className="d-inline-block text-left" style={{ fontSize: '0.75rem' }}>
                                <div
                                    onClick={this.toggleStatusExpanded}
                                    style={{ cursor: 'pointer' }}
                                    className="d-flex align-items-center mb-1"
                                >
                                    <strong className="mr-2">Job Status</strong>
                                    <FontAwesomeIcon
                                        icon={statusExpanded ? faChevronDown : faChevronRight}
                                        className="text-muted"
                                        style={{ width: '10px' }}
                                    />
                                </div>
                                {statusLines.map((line, idx) => {
                                    const { status } = this.getStageInfo(line);
                                    return (
                                        <div key={line.id || idx} className="d-flex align-items-center mb-1">
                                            <span className="text-muted text-capitalize mr-2" style={{ minWidth: '42px' }}>
                                                {line.type}
                                            </span>
                                            <Badge color={this.getQueueStatusColor(status)} style={{ fontSize: '0.7rem' }}>
                                                {status}
                                            </Badge>
                                        </div>
                                    );
                                })}
                                <Collapse isOpen={statusExpanded}>
                                    <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                        {statusLines.map((line, idx) => (
                                            <div key={line.id || idx}>
                                                <span className="text-capitalize">{line.type}</span>: {this.renderStageDates(line)}
                                            </div>
                                        ))}
                                    </div>
                                </Collapse>
                            </div>
                        ) : (
                            <div style={{ fontSize: '0.75rem', whiteSpace: 'pre' }} className="text-muted">
                                Status: {boxJob.status || boxJob.order_status}
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    {queueItems.map(this.renderQueueItem)}
                </div>

                {!boxJob.has_flag && boxJob.can_add_uv_product && (
                    <div className="text-right mt-2">
                        <Button
                            id={`addUVJobTooltip-${boxJob.id}`}
                            color="info"
                            size="sm"
                            onClick={this.handleAddUVJob}
                        >
                            Add UV Job
                        </Button>
                        <UncontrolledTooltip placement="top" target={`addUVJobTooltip-${boxJob.id}`}>
                            Please use this button if you want to attach UV printing to this job.
                        </UncontrolledTooltip>
                    </div>
                )}
            </div>
        );
    }
}

export default BatchBoxJobItem;
